import { useState, useCallback, useEffect, useRef, type RefObject } from 'react';
import type { MemeNFTElement, MemeTextElement } from '../types';
import { wrapText } from '../utils/canvasText';

// Resize handle types
export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | null;

export type NFTActionButtonId = 'layer';

export interface NFTActionButton {
  id: NFTActionButtonId;
  x: number;
  y: number;
  size: number;
}

const HANDLE_SIZE = 12; // Size of resize handle hit area
const MIN_SCALE = 0.05;
const MAX_SCALE = 5;

interface UseMemeInteractionOptions {
  canvasRef: RefObject<HTMLCanvasElement>;
  nftElements: MemeNFTElement[];
  textElements: MemeTextElement[];
  selectedElementId: string | null;
  selectedElementType: 'nft' | 'text' | null;
  canvasWidth: number;
  canvasHeight: number;
  onSelectElement: (elementId: string | null, elementType: 'nft' | 'text' | null) => void;
  onUpdateNFTPosition: (elementId: string, position: { x: number; y: number }) => void;
  onUpdateTextPosition: (elementId: string, position: { x: number; y: number }) => void;
  onUpdateNFTScale: (elementId: string, scale: number) => void;
  onUpdateTextFontSize: (elementId: string, fontSize: number) => void;
  onUpdateNFTRotation: (elementId: string, rotation: number) => void;
  onUpdateTextRotation: (elementId: string, rotation: number) => void;

  onBringToFront: (elementId: string, elementType: 'nft' | 'text') => void;
  onSendToBack: (elementId: string, elementType: 'nft' | 'text') => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onResizeStart: (handle: ResizeHandle) => void;
  onResizeEnd: () => void;
  onCharacterTap?: () => void;
  loadedImages: Map<string, HTMLImageElement>;
}

export interface SnapGuides {
  horizontal: boolean; // Snapped to horizontal center (shows vertical line)
  vertical: boolean;   // Snapped to vertical center (shows horizontal line)
}

const SNAP_THRESHOLD = 12; // Pixels from center to trigger snap

const TAP_MOVEMENT_THRESHOLD = 5; // Max pixels moved to still count as a tap

export function useMemeInteraction(options: UseMemeInteractionOptions) {
  const {
    canvasRef,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    canvasWidth,
    canvasHeight,
    onSelectElement,
    onUpdateNFTPosition,
    onUpdateTextPosition,
    onUpdateNFTScale,
    onUpdateTextFontSize,
    onUpdateNFTRotation,
    onUpdateTextRotation,
    onBringToFront,
    onSendToBack,
    onDragStart,
    onDragEnd,
    onResizeStart,
    onResizeEnd,
    onCharacterTap,
    loadedImages,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const hitTestCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hitTestCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const hitTestImageRef = useRef<HTMLImageElement | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [elementStartPos, setElementStartPos] = useState({ x: 0, y: 0 });
  const [snapGuides, setSnapGuides] = useState<SnapGuides>({ horizontal: false, vertical: false });

  // Tap detection state for character tap callback
  const [tapStartPos, setTapStartPos] = useState({ x: 0, y: 0 });
  const [wasAlreadySelected, setWasAlreadySelected] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [activeResizeHandle, setActiveResizeHandle] = useState<ResizeHandle>(null);
  const [resizeStartScale, setResizeStartScale] = useState(1);
  const [resizeStartFontSize, setResizeStartFontSize] = useState(48);
  const [resizeStartDistance, setResizeStartDistance] = useState(0);
  const [hoveredHandle, setHoveredHandle] = useState<ResizeHandle>(null);

  // Rotation state for mouse (Alt+drag)
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStartAngle, setRotationStartAngle] = useState(0);
  const [elementStartRotation, setElementStartRotation] = useState(0);

  // Calculate snapped position
  const getSnappedPosition = useCallback(
    (rawX: number, rawY: number): { x: number; y: number; snappedH: boolean; snappedV: boolean } => {
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;

      let snappedX = rawX;
      let snappedY = rawY;
      let snappedH = false;
      let snappedV = false;

      // Snap to horizontal center (element X near canvas center X)
      if (Math.abs(rawX - centerX) < SNAP_THRESHOLD) {
        snappedX = centerX;
        snappedH = true;
      }

      // Snap to vertical center (element Y near canvas center Y)
      if (Math.abs(rawY - centerY) < SNAP_THRESHOLD) {
        snappedY = centerY;
        snappedV = true;
      }

      return { x: snappedX, y: snappedY, snappedH, snappedV };
    },
    [canvasWidth, canvasHeight]
  );

  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;

      return {
        x: (screenX - rect.left) * scaleX,
        y: (screenY - rect.top) * scaleY,
      };
    },
    [canvasRef, canvasWidth, canvasHeight]
  );

  const getNFTElementBounds = useCallback(
    (element: MemeNFTElement) => {
      const image = loadedImages.get(element.nftData.mintAddress);
      if (!image) return null;

      const scaledWidth = image.width * element.scale;
      const scaledHeight = image.height * element.scale;

      return {
        left: element.position.x - scaledWidth / 2,
        top: element.position.y - scaledHeight / 2,
        right: element.position.x + scaledWidth / 2,
        bottom: element.position.y + scaledHeight / 2,
        width: scaledWidth,
        height: scaledHeight,
      };
    },
    [loadedImages]
  );

  // Pixel-accurate NFT hit testing so transparent pixels don't block characters behind.
  const isPointInNFTOpaquePixel = useCallback(
    (element: MemeNFTElement, canvasX: number, canvasY: number): boolean => {
      const image = loadedImages.get(element.nftData.mintAddress);
      if (!image || element.scale <= 0) return false;

      const dx = canvasX - element.position.x;
      const dy = canvasY - element.position.y;
      const r = (element.rotation * Math.PI) / 180;
      const c = Math.cos(r);
      const s = Math.sin(r);

      // Convert world-space point into the element's unrotated local space.
      const localX = dx * c + dy * s;
      const localY = -dx * s + dy * c;

      const drawWidth = image.width * element.scale;
      const drawHeight = image.height * element.scale;
      const imageX = (localX + drawWidth / 2) / element.scale;
      const imageY = (localY + drawHeight / 2) / element.scale;

      if (imageX < 0 || imageY < 0 || imageX >= image.width || imageY >= image.height) {
        return false;
      }

      let canvas = hitTestCanvasRef.current;
      let ctx = hitTestCtxRef.current;
      if (!canvas || !ctx) {
        canvas = document.createElement('canvas');
        ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return false;
        hitTestCanvasRef.current = canvas;
        hitTestCtxRef.current = ctx;
      }

      if (
        hitTestImageRef.current !== image ||
        canvas.width !== image.width ||
        canvas.height !== image.height
      ) {
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);
        hitTestImageRef.current = image;
      }

      const pixel = ctx.getImageData(Math.floor(imageX), Math.floor(imageY), 1, 1).data;
      return pixel[3] > 12;
    },
    [loadedImages]
  );

  const getNFTActionButtons = useCallback(
    (_element: MemeNFTElement): NFTActionButton[] => {
      return [];
    },
    []
  );

  const getTextElementBounds = useCallback(
    (element: MemeTextElement) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.font = `${element.fontSize}px ${element.fontFamily}`;
      const maxWidth = canvasWidth * 0.9;
      const lines = wrapText(ctx, element.text, maxWidth);
      const lineHeight = element.fontSize * 1.15;

      // Width is the widest line
      let width = 0;
      for (const line of lines) {
        const w = ctx.measureText(line).width;
        if (w > width) width = w;
      }

      const height = lines.length * lineHeight;

      return {
        left: element.position.x - width / 2,
        top: element.position.y - height / 2,
        right: element.position.x + width / 2,
        bottom: element.position.y + height / 2,
        width,
        height,
      };
    },
    [canvasRef, canvasWidth]
  );

  // Hit test for resize handles on selected element (NFT or text)
  const hitTestResizeHandle = useCallback(
    (canvasX: number, canvasY: number): ResizeHandle => {
      if (!selectedElementId || !selectedElementType) return null;

      let bounds: { left: number; top: number; right: number; bottom: number } | null = null;

      if (selectedElementType === 'nft') {
        const element = nftElements.find((e) => e.id === selectedElementId);
        if (!element) return null;
        if (element.stickerMode) return null;
        bounds = getNFTElementBounds(element);
      } else if (selectedElementType === 'text') {
        const element = textElements.find((e) => e.id === selectedElementId);
        if (!element) return null;
        bounds = getTextElementBounds(element);
        if (bounds) {
          // Expand bounds slightly to match the selection border padding
          bounds = {
            left: bounds.left - 5,
            top: bounds.top - 5,
            right: bounds.right + 5,
            bottom: bounds.bottom + 5,
          };
        }
      }

      if (!bounds) return null;

      const halfHandle = HANDLE_SIZE / 2;

      // Check corners first (higher priority)
      if (
        canvasX >= bounds.left - halfHandle &&
        canvasX <= bounds.left + halfHandle &&
        canvasY >= bounds.top - halfHandle &&
        canvasY <= bounds.top + halfHandle
      ) {
        return 'nw';
      }
      if (
        canvasX >= bounds.right - halfHandle &&
        canvasX <= bounds.right + halfHandle &&
        canvasY >= bounds.top - halfHandle &&
        canvasY <= bounds.top + halfHandle
      ) {
        return 'ne';
      }
      if (
        canvasX >= bounds.right - halfHandle &&
        canvasX <= bounds.right + halfHandle &&
        canvasY >= bounds.bottom - halfHandle &&
        canvasY <= bounds.bottom + halfHandle
      ) {
        return 'se';
      }
      if (
        canvasX >= bounds.left - halfHandle &&
        canvasX <= bounds.left + halfHandle &&
        canvasY >= bounds.bottom - halfHandle &&
        canvasY <= bounds.bottom + halfHandle
      ) {
        return 'sw';
      }

      // Check edges
      if (
        canvasX >= bounds.left + halfHandle &&
        canvasX <= bounds.right - halfHandle &&
        canvasY >= bounds.top - halfHandle &&
        canvasY <= bounds.top + halfHandle
      ) {
        return 'n';
      }
      if (
        canvasX >= bounds.left + halfHandle &&
        canvasX <= bounds.right - halfHandle &&
        canvasY >= bounds.bottom - halfHandle &&
        canvasY <= bounds.bottom + halfHandle
      ) {
        return 's';
      }
      if (
        canvasX >= bounds.left - halfHandle &&
        canvasX <= bounds.left + halfHandle &&
        canvasY >= bounds.top + halfHandle &&
        canvasY <= bounds.bottom - halfHandle
      ) {
        return 'w';
      }
      if (
        canvasX >= bounds.right - halfHandle &&
        canvasX <= bounds.right + halfHandle &&
        canvasY >= bounds.top + halfHandle &&
        canvasY <= bounds.bottom - halfHandle
      ) {
        return 'e';
      }

      return null;
    },
    [selectedElementId, selectedElementType, nftElements, textElements, getNFTElementBounds, getTextElementBounds]
  );

  const hitTestActionButton = useCallback(
    (canvasX: number, canvasY: number): NFTActionButtonId | null => {
      if (!selectedElementId || selectedElementType !== 'nft') return null;
      const element = nftElements.find((e) => e.id === selectedElementId);
      if (!element) return null;

      const buttons = getNFTActionButtons(element);
      for (const button of buttons) {
        if (
          canvasX >= button.x &&
          canvasX <= button.x + button.size &&
          canvasY >= button.y &&
          canvasY <= button.y + button.size
        ) {
          return button.id;
        }
      }

      return null;
    },
    [selectedElementId, selectedElementType, nftElements, getNFTActionButtons]
  );

  const handleActionButtonPress = useCallback(
    (actionId: NFTActionButtonId, elementId: string) => {
      const element = nftElements.find((el) => el.id === elementId);
      if (!element) return;

      switch (actionId) {
        case 'layer':
          // Cycle through zIndex: if at front, send to back; otherwise bring to front
          const maxZ = Math.max(...nftElements.map(e => e.zIndex));
          const elementZ = element.zIndex;
          if (elementZ >= maxZ) {
            onSendToBack(elementId, 'nft');
          } else {
            onBringToFront(elementId, 'nft');
          }
          break;
        default:
          break;
      }
    },
    [nftElements, onBringToFront, onSendToBack]
  );

  // Calculate distance from element center (used for resize calculations)
  const getDistanceFromCenter = useCallback(
    (canvasX: number, canvasY: number, elementId: string): number => {
      const element = nftElements.find((e) => e.id === elementId);
      if (!element) return 0;

      const dx = canvasX - element.position.x;
      const dy = canvasY - element.position.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    [nftElements]
  );

  // Calculate distance from text element center (used for text resize calculations)
  const getDistanceFromTextCenter = useCallback(
    (canvasX: number, canvasY: number, elementId: string): number => {
      const element = textElements.find((e) => e.id === elementId);
      if (!element) return 0;

      const dx = canvasX - element.position.x;
      const dy = canvasY - element.position.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    [textElements]
  );

  const hitTestElements = useCallback(
    (canvasX: number, canvasY: number, excludeId?: string): { id: string; type: 'nft' | 'text' } | null => {
      // Check text elements first (usually on top)
      const sortedTexts = [...textElements].sort((a, b) => b.zIndex - a.zIndex);
      for (const element of sortedTexts) {
        if (excludeId && element.id === excludeId) continue;
        const bounds = getTextElementBounds(element);
        if (!bounds) continue;

        if (
          canvasX >= bounds.left &&
          canvasX <= bounds.right &&
          canvasY >= bounds.top &&
          canvasY <= bounds.bottom
        ) {
          return { id: element.id, type: 'text' };
        }
      }

      // Check NFT elements
      const sortedNFTs = [...nftElements].sort((a, b) => b.zIndex - a.zIndex);
      for (const element of sortedNFTs) {
        if (excludeId && element.id === excludeId) continue;
        if (isPointInNFTOpaquePixel(element, canvasX, canvasY)) {
          return { id: element.id, type: 'nft' };
        }
      }

      return null;
    },
    [nftElements, textElements, getTextElementBounds, isPointInNFTOpaquePixel]
  );

  const hitTestNFTElementsAtPoint = useCallback(
    (canvasX: number, canvasY: number): string[] => {
      const sortedNFTs = [...nftElements].sort((a, b) => b.zIndex - a.zIndex);
      const hitIds: string[] = [];

      for (const element of sortedNFTs) {
        if (isPointInNFTOpaquePixel(element, canvasX, canvasY)) {
          hitIds.push(element.id);
        }
      }

      return hitIds;
    },
    [nftElements, isPointInNFTOpaquePixel]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;

      setHasMoved(false);
      const { x, y } = screenToCanvas(e.clientX, e.clientY);

      const actionHit = hitTestActionButton(x, y);
      if (actionHit && selectedElementId) {
        handleActionButtonPress(actionHit, selectedElementId);
        return;
      }

      // Check for resize handle first (works for both NFT and text elements)
      const resizeHandle = hitTestResizeHandle(x, y);
      if (resizeHandle && selectedElementId && selectedElementType) {
        if (selectedElementType === 'nft') {
          const element = nftElements.find((el) => el.id === selectedElementId);
          if (element && !element.stickerMode) {
            setIsResizing(true);
            setActiveResizeHandle(resizeHandle);
            setResizeStartScale(element.scale);
            setResizeStartDistance(getDistanceFromCenter(x, y, selectedElementId));
            onResizeStart(resizeHandle);
            return;
          }
        } else if (selectedElementType === 'text') {
          const element = textElements.find((el) => el.id === selectedElementId);
          if (element) {
            setIsResizing(true);
            setActiveResizeHandle(resizeHandle);
            setResizeStartFontSize(element.fontSize);
            setResizeStartDistance(getDistanceFromTextCenter(x, y, selectedElementId));
            onResizeStart(resizeHandle);
            return;
          }
        }
      }

      // Check for Alt+click on selected element to start rotation
      if (e.altKey && selectedElementId && selectedElementType) {
        const element = selectedElementType === 'nft' 
          ? nftElements.find((el) => el.id === selectedElementId)
          : textElements.find((el) => el.id === selectedElementId);
        if (element) {
          const dx = x - element.position.x;
          const dy = y - element.position.y;
          const angle = Math.atan2(dy, dx);
          setIsRotating(true);
          setRotationStartAngle(angle);
          setElementStartRotation(element.rotation);
          setDragStart({ x, y });
          return;
        }
      }

      const hitResult = hitTestElements(x, y);

      if (hitResult) {
        const isSameSelection =
          hitResult.id === selectedElementId && hitResult.type === selectedElementType;
        setWasAlreadySelected(isSameSelection && hitResult.type === 'nft');
        setTapStartPos({ x, y });
        onSelectElement(hitResult.id, hitResult.type);

        if (hitResult.type === 'nft') {
          const element = nftElements.find((el) => el.id === hitResult.id);
          if (element) {
            setIsDragging(true);
            setDragStart({ x, y });
            setElementStartPos(element.position);
            onDragStart();
          }
        } else {
          const element = textElements.find((el) => el.id === hitResult.id);
          if (element) {
            setIsDragging(true);
            setDragStart({ x, y });
            setElementStartPos(element.position);
            onDragStart();
          }
        }
      } else {
        onSelectElement(null, null);
      }
    },
    [
      screenToCanvas,
      hitTestResizeHandle,
      hitTestElements,
      hitTestNFTElementsAtPoint,
      selectedElementId,
      selectedElementType,
      nftElements,
      textElements,
      onSelectElement,
      onDragStart,
      onResizeStart,
      getDistanceFromCenter,
      getDistanceFromTextCenter,
      hitTestActionButton,
      handleActionButtonPress,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { x, y } = screenToCanvas(e.clientX, e.clientY);

      // Track whether mouse has moved beyond tap threshold
      if (isDragging && !hasMoved) {
        const dx = x - tapStartPos.x;
        const dy = y - tapStartPos.y;
        if (Math.abs(dx) > TAP_MOVEMENT_THRESHOLD || Math.abs(dy) > TAP_MOVEMENT_THRESHOLD) {
          setHasMoved(true);
        }
      }

      // Handle resize operation
      if (isResizing && selectedElementId && activeResizeHandle) {
        if (selectedElementType === 'nft') {
          const currentDistance = getDistanceFromCenter(x, y, selectedElementId);
          if (resizeStartDistance > 0) {
            const scaleFactor = currentDistance / resizeStartDistance;
            const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizeStartScale * scaleFactor));
            onUpdateNFTScale(selectedElementId, newScale);
          }
        } else if (selectedElementType === 'text') {
          const currentDistance = getDistanceFromTextCenter(x, y, selectedElementId);
          if (resizeStartDistance > 0) {
            const scaleFactor = currentDistance / resizeStartDistance;
            const newFontSize = Math.max(12, Math.min(200, Math.round(resizeStartFontSize * scaleFactor)));
            onUpdateTextFontSize(selectedElementId, newFontSize);
          }
        }
        return;
      }

      // Handle rotation operation (Alt+drag)
      if (isRotating && selectedElementId && selectedElementType) {
        const element = selectedElementType === 'nft'
          ? nftElements.find((el) => el.id === selectedElementId)
          : textElements.find((el) => el.id === selectedElementId);
        if (element) {
          const dx = x - element.position.x;
          const dy = y - element.position.y;
          const currentAngle = Math.atan2(dy, dx);
          const angleDelta = currentAngle - rotationStartAngle;
          const newRotation = normalizeRotation(elementStartRotation + (angleDelta * 180) / Math.PI);
          
          if (selectedElementType === 'nft') {
            onUpdateNFTRotation(selectedElementId, newRotation);
          } else {
            onUpdateTextRotation(selectedElementId, newRotation);
          }
        }
        return;
      }

      // Update hover state for cursor feedback (only when not dragging/resizing/rotating)
      if (!isDragging && !isResizing && !isRotating) {
        const handle = hitTestResizeHandle(x, y);
        setHoveredHandle(handle);
      }

      if (isDragging && selectedElementId) {
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        const rawX = elementStartPos.x + deltaX;
        const rawY = elementStartPos.y + deltaY;

        // Apply snap alignment
        const { x: snappedX, y: snappedY, snappedH, snappedV } = getSnappedPosition(rawX, rawY);
        setSnapGuides({ horizontal: snappedH, vertical: snappedV });

        if (selectedElementType === 'nft') {
          onUpdateNFTPosition(selectedElementId, {
            x: snappedX,
            y: snappedY,
          });
        } else if (selectedElementType === 'text') {
          onUpdateTextPosition(selectedElementId, {
            x: snappedX,
            y: snappedY,
          });
        }
      }
    },
    [
      screenToCanvas,
      isDragging,
      isResizing,
      isRotating,
      selectedElementId,
      selectedElementType,
      activeResizeHandle,
      dragStart,
      elementStartPos,
      resizeStartDistance,
      resizeStartScale,
      rotationStartAngle,
      elementStartRotation,
      onUpdateNFTPosition,
      onUpdateTextPosition,
      onUpdateNFTScale,
      onUpdateTextFontSize,
      onUpdateNFTRotation,
      onUpdateTextRotation,
      getSnappedPosition,
      getDistanceFromCenter,
      getDistanceFromTextCenter,
      hitTestResizeHandle,
      hasMoved,
      tapStartPos,
      nftElements,
      textElements,
      resizeStartFontSize,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Tap on selected NFT: cycle through overlaps; otherwise keep existing tap behavior.
      if (!hasMoved && wasAlreadySelected && selectedElementType === 'nft') {
        const overlappingNFTIds = hitTestNFTElementsAtPoint(tapStartPos.x, tapStartPos.y);
        const selectedIndex = selectedElementId
          ? overlappingNFTIds.indexOf(selectedElementId)
          : -1;

        if (selectedIndex >= 0 && overlappingNFTIds.length > 1) {
          const nextId = overlappingNFTIds[(selectedIndex + 1) % overlappingNFTIds.length];
          onSelectElement(nextId, 'nft');
        } else {
          onCharacterTap?.();
        }
      }
      setIsDragging(false);
      setSnapGuides({ horizontal: false, vertical: false });
      onDragEnd();
    }
    if (isResizing) {
      setIsResizing(false);
      setActiveResizeHandle(null);
      onResizeEnd();
    }
    if (isRotating) {
      setIsRotating(false);
    }
  }, [isDragging, isResizing, isRotating, hasMoved, wasAlreadySelected, selectedElementType, selectedElementId, tapStartPos, hitTestNFTElementsAtPoint, onSelectElement, onCharacterTap, onDragEnd, onResizeEnd]);

  // State for pinch-to-resize on touch
  const [lastPinchDistance, setLastPinchDistance] = useState<number | null>(null);
  const [lastPinchAngle, setLastPinchAngle] = useState<number | null>(null);
  const [resizeStartRotation, setResizeStartRotation] = useState(0);

  const getTouchDistance = (touches: TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchAngle = (touches: TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.atan2(dy, dx);
  };

  const normalizeRotation = (rotation: number): number => {
    const normalized = ((rotation + 180) % 360 + 360) % 360 - 180;
    return normalized;
  };

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      // Handle pinch-to-resize/rotate when an element is selected
      if (e.touches.length === 2 && selectedElementId && selectedElementType) {
        const distance = getTouchDistance(e.touches);
        const angle = getTouchAngle(e.touches);

        if (selectedElementType === 'nft') {
          const element = nftElements.find((el) => el.id === selectedElementId);
          if (element) {
            setLastPinchDistance(distance);
            setLastPinchAngle(angle);
            setResizeStartScale(element.scale);
            setResizeStartRotation(element.rotation);
            setIsResizing(true);
            onResizeStart('se'); // Use corner handle for pinch
            return;
          }
        }

        if (selectedElementType === 'text') {
          const element = textElements.find((el) => el.id === selectedElementId);
          if (element) {
            setLastPinchDistance(distance);
            setLastPinchAngle(angle);
            setResizeStartRotation(element.rotation);
            setIsResizing(true);
            onResizeStart('se');
            return;
          }
        }
      }

      if (e.touches.length !== 1) return;

      setHasMoved(false);
      const touch = e.touches[0];
      const { x, y } = screenToCanvas(touch.clientX, touch.clientY);

      const actionHit = hitTestActionButton(x, y);
      if (actionHit && selectedElementId) {
        handleActionButtonPress(actionHit, selectedElementId);
        return;
      }

      const hitResult = hitTestElements(x, y);

      if (hitResult) {
        const isSameSelection =
          hitResult.id === selectedElementId && hitResult.type === selectedElementType;
        setWasAlreadySelected(isSameSelection && hitResult.type === 'nft');
        setTapStartPos({ x, y });
        onSelectElement(hitResult.id, hitResult.type);

        if (hitResult.type === 'nft') {
          const element = nftElements.find((el) => el.id === hitResult.id);
          if (element) {
            setIsDragging(true);
            setDragStart({ x, y });
            setElementStartPos(element.position);
            onDragStart();
          }
        } else {
          const element = textElements.find((el) => el.id === hitResult.id);
          if (element) {
            setIsDragging(true);
            setDragStart({ x, y });
            setElementStartPos(element.position);
            onDragStart();
          }
        }
      } else {
        onSelectElement(null, null);
      }
    },
    [
      screenToCanvas,
      hitTestElements,
      hitTestNFTElementsAtPoint,
      selectedElementId,
      selectedElementType,
      nftElements,
      textElements,
      onSelectElement,
      onDragStart,
      onResizeStart,
      getTouchDistance,
      getTouchAngle,
      hitTestActionButton,
      handleActionButtonPress,
    ]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // Track movement for tap detection on touch
      if (e.touches.length === 1 && isDragging && !hasMoved) {
        const touch = e.touches[0];
        const { x, y } = screenToCanvas(touch.clientX, touch.clientY);
        const dx = x - tapStartPos.x;
        const dy = y - tapStartPos.y;
        if (Math.abs(dx) > TAP_MOVEMENT_THRESHOLD || Math.abs(dy) > TAP_MOVEMENT_THRESHOLD) {
          setHasMoved(true);
        }
      }

      // Handle pinch-to-resize and rotate
      if (e.touches.length === 2 && isResizing && selectedElementId) {
        if (lastPinchAngle !== null) {
          const currentAngle = getTouchAngle(e.touches);
          const angleDelta = currentAngle - lastPinchAngle;
          const newRotation = normalizeRotation(resizeStartRotation + (angleDelta * 180) / Math.PI);

          if (selectedElementType === 'nft') {
            onUpdateNFTRotation(selectedElementId, newRotation);
          } else if (selectedElementType === 'text') {
            onUpdateTextRotation(selectedElementId, newRotation);
          }
        }

        // Handle pinch scaling for NFT (text uses fontSize, not scale)
        if (selectedElementType === 'nft' && lastPinchDistance !== null) {
          const element = nftElements.find((el) => el.id === selectedElementId);
          if (element && !element.stickerMode) {
            const currentDistance = getTouchDistance(e.touches);
            const scaleFactor = currentDistance / lastPinchDistance;
            const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizeStartScale * scaleFactor));
            onUpdateNFTScale(selectedElementId, newScale);
          }
        }
        return;
      }

      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const { x, y } = screenToCanvas(touch.clientX, touch.clientY);

      if (isDragging && selectedElementId) {
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        const rawX = elementStartPos.x + deltaX;
        const rawY = elementStartPos.y + deltaY;

        // Apply snap alignment
        const { x: snappedX, y: snappedY, snappedH, snappedV } = getSnappedPosition(rawX, rawY);
        setSnapGuides({ horizontal: snappedH, vertical: snappedV });

        if (selectedElementType === 'nft') {
          onUpdateNFTPosition(selectedElementId, {
            x: snappedX,
            y: snappedY,
          });
        } else if (selectedElementType === 'text') {
          onUpdateTextPosition(selectedElementId, {
            x: snappedX,
            y: snappedY,
          });
        }
      }
    },
    [
      screenToCanvas,
      isDragging,
      isResizing,
      selectedElementId,
      selectedElementType,
      dragStart,
      elementStartPos,
      lastPinchDistance,
      lastPinchAngle,
      resizeStartScale,
      resizeStartRotation,
      onUpdateNFTPosition,
      onUpdateTextPosition,
      onUpdateNFTScale,
      onUpdateNFTRotation,
      onUpdateTextRotation,
      getSnappedPosition,
      getTouchDistance,
      getTouchAngle,
      normalizeRotation,
      nftElements,
      hasMoved,
      tapStartPos,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      // Tap on selected NFT: cycle through overlaps; otherwise keep existing tap behavior.
      if (!hasMoved && wasAlreadySelected && selectedElementType === 'nft') {
        const overlappingNFTIds = hitTestNFTElementsAtPoint(tapStartPos.x, tapStartPos.y);
        const selectedIndex = selectedElementId
          ? overlappingNFTIds.indexOf(selectedElementId)
          : -1;

        if (selectedIndex >= 0 && overlappingNFTIds.length > 1) {
          const nextId = overlappingNFTIds[(selectedIndex + 1) % overlappingNFTIds.length];
          onSelectElement(nextId, 'nft');
        } else {
          onCharacterTap?.();
        }
      }
      setIsDragging(false);
      setSnapGuides({ horizontal: false, vertical: false });
      onDragEnd();
    }
    if (isResizing) {
      setIsResizing(false);
      setActiveResizeHandle(null);
      setLastPinchDistance(null);
      setLastPinchAngle(null);
      onResizeEnd();
    }
  }, [isDragging, isResizing, hasMoved, wasAlreadySelected, selectedElementType, selectedElementId, tapStartPos, hitTestNFTElementsAtPoint, onSelectElement, onCharacterTap, onDragEnd, onResizeEnd]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [
    canvasRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  return {
    isDragging,
    isResizing,
    isRotating,
    hoveredHandle,
    snapGuides,
    getNFTElementBounds,
    getTextElementBounds,
    getNFTActionButtons,
  };
}
